import {
  Circle,
  Defs,
  Document,
  Font,
  Image,
  LinearGradient,
  Page,
  Stop,
  StyleSheet,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";
import { useEffect, useState } from "react";
import ManropeRegular from "../../assets/fonts/manrope/Manrope-Regular.ttf";
import ManropeMedium from "../../assets/fonts/manrope/Manrope-Medium.ttf";
import ManropeSemiBold from "../../assets/fonts/manrope/Manrope-SemiBold.ttf";
import ManropeBold from "../../assets/fonts/manrope/Manrope-Bold.ttf";
import moment from "moment";
import { formattedCurrency } from "./calculateTime";

Font.register({
  family: "Manrope",
  fonts: [
    {
      src: ManropeRegular,
      fontWeight: 400,
    },
    {
      src: ManropeMedium,
      fontWeight: 500,
    },
    {
      src: ManropeSemiBold,
      fontWeight: 600,
    },
    {
      src: ManropeBold,
      fontWeight: 700,
    },
  ],
});

const defaultCellHeight = 25;
const defaultCellWidth = ["55%", "10%", "20%", "15%"];
const defaultBorder = "1px dashed #323232";

const commonCellStyle = {
  borderBottom: defaultBorder,
  minHeight: defaultCellHeight,
  height: "100%",
  paddingVertical: 3,
};

const RadioCheckedSVG = ({ checked }) => (
  <Svg height="16" width="16">
    <Defs>
      <LinearGradient
        id="linear-gradient"
        x1="0.5"
        y1="0.019"
        x2="0.537"
        y2="1.547"
      >
        <Stop offset="0" stopColor="#00246a" />
        <Stop offset="1" stopColor="#95a7c6" />
      </LinearGradient>
    </Defs>
    <Circle
      cx="8"
      cy="8"
      r="6"
      stroke="grey"
      strokeWidth={checked ? "0" : "0.5"}
      fill={checked ? "url(#linear-gradient)" : "#FFFFFF"}
    />
  </Svg>
);

const RenderFooter = ({ showCompanyDetails, data }) => {
  return (
    <View fixed style={styles.footerContainer}>
      <View>
        <Text
          style={styles.companyDetailsName}
          render={({ pageNumber, totalPages }) => (
            <>
              {showCompanyDetails || pageNumber == totalPages
                ? data.company.name
                : ""}
            </>
          )}
        />
        <Text
          style={styles.companyDetailsAddress}
          render={({ pageNumber, totalPages }) => (
            <>
              {showCompanyDetails || pageNumber == totalPages
                ? data.company.address
                : ""}
            </>
          )}
        />
        <Text
          style={styles.companyDetailsVatNo}
          render={({ pageNumber, totalPages }) => (
            <>
              {showCompanyDetails || pageNumber == totalPages
                ? `VAT No. ${data.company.vatNo}`
                : ""}
            </>
          )}
        />
      </View>
      <View>
        <Text
          style={styles.footerPagination}
          render={({ pageNumber, totalPages }) => (
            <>{`Pag. ${pageNumber}/${totalPages}`}</>
          )}
        />
      </View>
    </View>
  );
};

const RenderSection = ({ section }) => {
  const parseHtml = (html) => {
    return html
      .replace(/<\/p>/g, "\n\n")
      .replace(/<br\s*\/?>/g, "\n")
      .replace(/<[^>]+>/g, "");
  };

  const RenderDescriptionSection = ({ label }) => {
    if (!label) return null;
    return (
      <View>
        <Text
          style={{
            fontSize: 12,
            color: "#323232",
            opacity: 0.5,
          }}
        >
          {parseHtml(label)}
        </Text>
      </View>
    );
  };

  return (
    <>
      <View style={styles.sectionContainer}>
        <View style={styles.sectionHeader}>
          <View style={styles.sectionHeaderViewContainer}>
            <Text style={styles.sectionName}>{section.sectionName}</Text>
            <Text style={styles.sectionStatus}>Status</Text>
          </View>
        </View>
        <View style={styles.sectionDetail}>
          <View style={styles.sectionDetailViewContainer}>
            <View style={styles.subSectionHeaderContainer}>
              <Text style={styles.subSectionHeaderMatrial}>Material</Text>
              <Text style={styles.subSectionHeaderQty}>Qty</Text>
              <Text style={styles.subSectionHeaderTotal}>Total</Text>
              <Text style={styles.subSectionHeaderApprove}>Approve</Text>
            </View>
            {section.subSections.map((subsection, subsectionIndex) => (
              <RenderSubSection
                key={`pdf-sub-section-${subsectionIndex}`}
                subsection={subsection}
                last={(section.subSections?.length || 0) - 1 == subsectionIndex}
              />
            ))}

            <div style={{ marginTop: 5 }}>
              <RenderDescriptionSection label={section.description} />
            </div>

            <View style={{ marginTop: 10 }}>
              <RenderTotalSection
                label={"Total Section"}
                total={formattedCurrency(section.totalAmount)}
              />
            </View>
          </View>
        </View>
      </View>
    </>
  );
};

const RenderSubSection = ({ subsection, last }) => {
  return (
    <>
      <View style={styles.subSectionBodyContainer} wrap={false}>
        <View style={styles.subSectionBodyMatrial}>
          <Text style={styles.subSectionBodyMatrialCode}>
            {subsection.productPartCode}
          </Text>
          <Text>{subsection.productPartName}</Text>
        </View>
        <Text style={styles.subSectionBodyQty}>{subsection.qty}</Text>
        <View
          style={[
            styles.subSectionBodyTotal,
            last ? { borderBottom: defaultBorder } : {},
          ]}
        >
          <Text>{formattedCurrency(subsection.total)}</Text>
        </View>
        <View style={styles.subSectionBodyStatus}>
          <RadioCheckedSVG checked={Boolean(subsection.isApproved)} />
        </View>
      </View>
    </>
  );
};

const RenderTotalSection = ({ label, total }) => {
  return (
    <View style={styles.totalSectionContainer}>
      <Text style={styles.totalSectionLabel}>{label}</Text>
      <Text style={styles.totalSectionTotal}>{total}</Text>
    </View>
  );
};

const RenderPlaceholderBox = ({ text }) => {
  return (
    <View style={styles.placeholderBox}>
      <Text style={styles.placeholderLabel}>{text}</Text>
    </View>
  );
};

const QuotePdfDocument = ({ quote }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (quote) {
      setData(quote);
    }
  }, [JSON.stringify(quote)]);

  if (!data) {
    return <></>;
  }

  const hasClient = (client) => {
    const clientItems = [
      Boolean(client?.company),
      Boolean(client?.name),
      Boolean(client?.address),
      Boolean(client?.email),
    ];
    return !clientItems.every((x) => x == false);
  };

  let workspaceName = localStorage.getItem("workspaceName")?.split("-")[0];
  let selectedProject = localStorage.getItem("projectForPrint");

  // let pdfTitle = `${data.client.name}-${selectedProject}-${workspaceName}-${
  //   data.quoteCode
  // }-${moment(data.updatedDate).format("YYYY.MM.DD")}.pdf`;

  let pdfTitle = `${data.client.name}-${selectedProject}-${workspaceName}-${
    data.quoteCode
  }-${moment(new Date()).format("YYYY.MM.DD, h:mm a")}.pdf`;

  console.log("asc56 printData", data);

  return (
    <Document title={pdfTitle}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View>
            {Boolean(data.company.logo) && (
              <Image
                src={`data:image/png;base64,${data.company.logo}`}
                style={{ width: 50, height: 65 }}
              />
            )}
          </View>
          <View style={styles.clientContainer}>
            {hasClient(data.client) && (
              <>
                <Text>To,</Text>
                <Text style={{ fontWeight: "bold" }}>
                  {data.client.company}
                </Text>
                <Text>{data.client.name}</Text>
                <Text>{data.client.address}</Text>
                <Text>{data.client.email}</Text>
              </>
            )}
          </View>
        </View>
        <View>
          <Text style={styles.commercialTitle}>Commercial Proposal</Text>
          <Text style={styles.commercialDescription}>
            This document is an official quotation for client's review and
            approval. Outlined in the document is a detailed breakdown of
            expenses encompassing labor, materials, and associated costs
            required for the successful completion of the project/milestone of a
            project. In confirming your acceptance of the quote, your signature
            on this document signifies acknowledgment and agreement to all
            outlined expenses. To facilitate a smooth progression of the
            project, we kindly request that you sign and send digitally a
            document at the nearest time.
          </Text>
        </View>

        <RenderFooter data={data} showCompanyDetails={true} />
      </Page>
      <Page size="A4" style={styles.quotePage} break>
        <Text
          fixed
          debug={false}
          style={styles.quoteCodeHeaderLeft}
          render={({ pageNumber, totalPages }) => `Quote`}
        />
        <Text
          fixed
          debug={false}
          style={styles.quoteCodeHeaderRightTop}
          render={({ pageNumber, totalPages }) => `ID ${data.quoteCode}`}
        />
        <Text
          fixed
          debug={false}
          style={styles.quoteCodeHeaderRightBottom}
          render={({ pageNumber, totalPages }) =>
            moment(data.updatedDate).format("DD.MM.YYYY")
          }
        />
        {data.sections.map((section, sectionindex) => (
          <RenderSection
            key={`pdf-section-${sectionindex}`}
            section={section}
          />
        ))}
        <View style={styles.quoteTotalSection}>
          <RenderTotalSection
            label={"Total Quote"}
            total={formattedCurrency(data.total)}
          />
        </View>
        <View style={styles.placeholderContainer} wrap={false}>
          <View style={styles.placeholderItemRow}>
            <RenderPlaceholderBox text={"Date"} />
            <RenderPlaceholderBox text={"Date"} />
          </View>
          <View style={styles.placeholderItemRow}>
            <RenderPlaceholderBox text={"Spire Signature"} />
            <RenderPlaceholderBox text={"Client Signature"} />
          </View>
        </View>

        <RenderFooter data={data} showCompanyDetails={false} />
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    display: "flex",
    backgroundColor: "#FAFAFA",
    paddingTop: 26,
    paddingBottom: 60,
    paddingHorizontal: 50,
    fontFamily: "Manrope",
  },
  quotePage: {
    display: "flex",
    backgroundColor: "#FAFAFA",
    paddingTop: 82,
    paddingBottom: 60,
    paddingHorizontal: 50,
    fontFamily: "Manrope",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  clientContainer: {
    fontWeight: "medium",
    color: "#323232",
    fontSize: 14,
    paddingTop: 50,
    width: "40%",
  },
  commercialTitle: {
    color: "#323232",
    fontSize: 20,
    fontWeight: "bold",
    letterSpacing: 0.6,
    paddingTop: 40,
    paddingBottom: 45,
  },
  commercialDescription: {
    color: "#7E7E7E",
    fontSize: 12,
    letterSpacing: 0.36,
  },
  footerContainer: {
    position: "absolute",
    bottom: 13,
    left: 50,
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  footerPagination: {
    color: "#333333",
    fontSize: 10,
    textAlign: "right",
  },
  companyDetailsName: {
    color: "#333333",
    fontSize: 10,
    fontWeight: "bold",
  },
  companyDetailsAddress: {
    color: "#333333",
    fontSize: 10,
    marginBottom: 5,
    maxWidth: 220,
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
  },
  companyDetailsVatNo: {
    color: "#333333",
    fontSize: 10,
  },
  quoteCodeHeaderLeft: {
    fontFamily: "Manrope",
    fontWeight: "bold",
    fontSize: 14,
    color: "#333333",
    backgroundColor: "#FFFFFF",
    position: "absolute",
    top: 26,
    right: 150,
    height: 50,
    width: 100,
    paddingLeft: 20,
    paddingTop: 6,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  },
  quoteCodeHeaderRightTop: {
    fontFamily: "Manrope",
    fontWeight: "bold",
    fontSize: 14,
    color: "#333333",
    backgroundColor: "#FFFFFF",
    position: "absolute",
    right: 50,
    top: 26,
    width: 100,
    paddingTop: 6,
    height: 30,
    borderTopRightRadius: 8,
  },
  quoteCodeHeaderRightBottom: {
    fontFamily: "Manrope",
    fontSize: 10,
    color: "#333333",
    backgroundColor: "#FFFFFF",
    position: "absolute",
    right: 50,
    top: 56,
    width: 100,
    height: 20,
    borderBottomRightRadius: 8,
  },
  sectionContainer: {
    borderRadius: 8,
    overflow: "hidden",
  },
  sectionHeaderViewContainer: {
    paddingLeft: 35,
    paddingRight: 12,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  sectionDetailViewContainer: {
    paddingLeft: 35,
    paddingRight: 12,
    display: "flex",
    flexDirection: "column",
  },
  sectionHeader: {
    height: 32,
    backgroundColor: "#CFD9E4",
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
  },
  sectionName: {
    fontSize: 12,
    color: "#323232",
    fontWeight: "bold",
  },
  sectionStatus: {
    fontSize: 12,
    color: "#323232",
    width: defaultCellWidth[3],
    textAlign: "center",
  },
  sectionDetail: {
    backgroundColor: "#FFFFFF",
    paddingVertical: 10,
  },
  subSectionHeaderContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
  },
  subSectionHeaderMatrial: {
    color: "#9A9999",
    fontSize: 10,
    width: defaultCellWidth[0],
    height: defaultCellHeight,
    borderBottom: defaultBorder,
    display: "flex",
    alignItems: "center",
  },
  subSectionHeaderQty: {
    color: "#9A9999",
    fontSize: 10,
    width: defaultCellWidth[1],
    height: defaultCellHeight,
    borderBottom: defaultBorder,
    textAlign: "center",
  },
  subSectionHeaderTotal: {
    color: "#323232",
    fontSize: 10,
    width: defaultCellWidth[2],
    height: defaultCellHeight,
    borderBottom: defaultBorder,
    textAlign: "right",
    paddingRight: 5,
  },
  subSectionHeaderApprove: {
    color: "#323232",
    fontSize: 10,
    width: defaultCellWidth[3],
    height: defaultCellHeight,
    borderBottom: defaultBorder,
    textAlign: "center",
  },
  subSectionBodyContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  subSectionBodyMatrial: {
    color: "#323232",
    fontSize: 10,
    width: defaultCellWidth[0],
    ...commonCellStyle,
  },
  subSectionBodyMatrialCode: {
    opacity: 0.6,
  },
  subSectionBodyQty: {
    color: "#323232",
    fontSize: 10,
    width: defaultCellWidth[1],
    ...commonCellStyle,
    textAlign: "center",
  },
  subSectionBodyTotal: {
    color: "#323232",
    fontSize: 10,
    width: defaultCellWidth[2],
    ...commonCellStyle,
    borderBottom: "none",
    textAlign: "right",
    paddingRight: 5,
    fontWeight: "bold",
    backgroundColor: "#CFD9E4",
    borderBottom: "1px solid #CFD9E4",
  },
  subSectionBodyStatus: {
    width: defaultCellWidth[3],
    ...commonCellStyle,
    display: "flex",
    alignItems: "center",
  },
  totalSectionContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    borderTop: defaultBorder,
    borderBottom: defaultBorder,
  },
  totalSectionLabel: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#323232",
    ...commonCellStyle,
    borderBottom: "none",
    width: defaultCellWidth[2],
  },
  totalSectionTotal: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#323232",
    ...commonCellStyle,
    borderBottom: "none",
    width: defaultCellWidth[0],
    marginRight: defaultCellWidth[3],
    textAlign: "right",
    paddingRight: 5,
  },
  quoteTotalSection: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    paddingLeft: 35,
    paddingRight: 12,
    paddingVertical: 20,
    marginTop: 10,
  },
  placeholderContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    marginTop: 10,
    paddingLeft: 35,
    paddingRight: 12,
  },
  placeholderItemRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  placeholderBox: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    height: 100,
    width: "40%",
  },
  placeholderLabel: {
    width: "100%",
    borderTop: "1px solid #A5A5A5",
    paddingTop: 4,
    color: "#323232",
    fontSize: 12,
    textAlign: "center",
  },
});

export default QuotePdfDocument;
